import { getCustomer } from '@/api/customer-api';
import { getLastLiveCommentByUser } from '@/api/fbapi';
import { asignUser, getOrderReferences } from '@/api/order-api';
import { useChat } from '@/contexts/ChatContext';
import { useChatCustomerOrders } from '@/hooks/use-chat-customer-orders';
import { toast } from '@/hooks/use-toast';
import { routes } from '@/routes/route-constants';
import { Participant } from '@/types/fb-type';
import { OrderReferenceItem, SourceType, TOrderWithRelation } from '@/types/order-type';
import {
  Calendar,
  Eye,
  FileText,
  MapPin,
  MessageCircle,
  MessageSquare,
  Phone,
  Plus,
  Reply,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { navigate } from 'wouter/use-browser-location';

import CustomerEditDialog from '../customers/CustomerEditDialog';
import ReplyComponent from '../facebook/ReplyComponent';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { AssignUserDialog } from './AssignUserDialog';
import CreateOrderDialog from './CreateOrderDialog';
import { InvoiceDialog } from './InvoiceDialog';
import OrderChatCell, { formatOrderDate } from './OrderChatCell';
import { ReceiptDialog } from './ReceiptDialog';
import ViewOrderDialog from './ViewOrderDialog';

const DEFAULT_COMMENT_REPLY = 'Please reply message /តបឆាតផងបង';

interface OrderDetailsProps {
  currentCustomer?: Participant;
  onOpenOrderDialog?: () => void;
  onViewOrderCallBack?: (currentOrder: TOrderWithRelation) => void;
  onConfirmOrderCallBack?: (status: boolean, currentOrder: TOrderWithRelation) => void;
}

export default function OrderDetailsChatPanel(props: OrderDetailsProps) {
  return <CustomerOrderPanel key={`${props.currentCustomer?.id ?? ''}:${props.currentCustomer?.conversationId ?? ''}`} {...props} />;
}

function CustomerOrderPanel({
  currentCustomer,
  onOpenOrderDialog,
  onViewOrderCallBack,
  onConfirmOrderCallBack,
}: OrderDetailsProps) {
  const { updateConversationAssignment } = useChat();
  const { customer, setCustomer, orders, loadingOrders, ordersError, refreshOrders } = useChatCustomerOrders(currentCustomer);
  const [isOpenOrderDialog, setIsOpenOrderDialog] = useState<boolean>(false);
  const [selectedOrderRelation, setSelectedOrderRelation] = useState<TOrderWithRelation | null>(
    null,
  );
  const [isOpenViewOrderDialog, setIsOpenViewOrderDialog] = useState<boolean>(false);
  const [isOpenInvoiceDialog, setIsOpenInvoiceDialog] = useState<boolean>(false);
  const [isOpenReceiptDialog, setIsOpenReceiptDialog] = useState<boolean>(false);
  const [isOpenEditCustomerDialog, setIsOpenEditCustomerDialog] = useState<boolean>(false);
  const [isOpenAssignDialog, setIsOpenAssignDialog] = useState<boolean>(false);

  const [lastOrderReferences, setLastOrderReferences] = useState<OrderReferenceItem[]>([]);
  const [fallbackComment, setFallbackComment] = useState<{
    commentId: string;
    message: string;
    fromName: string;
  } | null>(null);
  const [replyingCommentId, setReplyingCommentId] = useState<string | null>(null);

  const onSaveCustomer = async (customerId: string) => {
    const fetchCustomerResponse = await getCustomer(customerId);
    if (fetchCustomerResponse.success) {
      setCustomer(fetchCustomerResponse.data);
    } else {
      toast({
        title: 'Error',
        description: fetchCustomerResponse.message || 'Unexpected error for fetching  customer',
        variant: 'destructive',
      });
    }
  };

  const activeLastOrder = useMemo(() => (orders.length > 0 ? orders[0] : null), [orders]);
  const historyOrders = useMemo(() => (orders.length > 1 ? orders.slice(1) : []), [orders]);

  useEffect(() => {
    setReplyingCommentId(null);
    setLastOrderReferences([]);
    if (!activeLastOrder?.order?.id) {
      setLastOrderReferences([]);
      return;
    }
    let cancelled = false;
    getOrderReferences(String(activeLastOrder.order.id))
      .then((res) => {
        if (!cancelled && res.success) {
          setLastOrderReferences(res.data ?? []);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [activeLastOrder?.order?.id]);

  useEffect(() => {
    setFallbackComment(null);
    if (!currentCustomer?.id) {
      return;
    }
    let cancelled = false;
    getLastLiveCommentByUser(currentCustomer.id)
      .then((res) => {
        if (!cancelled && res.success) {
          setFallbackComment(res.data ?? null);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [currentCustomer?.id]);

  const onSubmitOrder = async (success: boolean, orderId?: string | null) => {
    setIsOpenOrderDialog(false);
    if (success && orderId && customer) {
      await refreshOrders();
    }
  };
  const onViewOrder = (orderWithRelation: TOrderWithRelation) => {
    setSelectedOrderRelation(orderWithRelation);
    setIsOpenViewOrderDialog(true);
  };
  const onConfirmOrder = async (status: boolean, orderWithRelation: TOrderWithRelation) => {
    if (customer) {
      await refreshOrders();
    }
    if (onConfirmOrderCallBack) {
      onConfirmOrderCallBack(status, orderWithRelation);
    }
  };
  const onViewReceipt = (order: TOrderWithRelation) => {
    setSelectedOrderRelation(order);
    setIsOpenReceiptDialog(true);
  };
  const onOpenAssignUser = (order: TOrderWithRelation) => {
    setSelectedOrderRelation(order);
    setIsOpenAssignDialog(true);
  };

  const handleAssignUserSubmit = async (userId: string, orderId: string) => {
    const res = await asignUser(orderId, userId);
    if (!res.success) {
      toast({
        title: 'Error',
        description: res.message || 'Failed to assign user',
        variant: 'destructive',
      });
      throw new Error(res.message || 'Failed to assign user');
    }
    toast({
      title: 'Success',
      description: `Order assigned to ${userId}`,
    });
    if (currentCustomer?.id) {
      updateConversationAssignment?.(currentCustomer.id, userId);
    }
    if (customer) {
      await refreshOrders();
    }
  };

  const resolveCommentIdForRef = (ref: OrderReferenceItem): string | null => {
    if (ref.referenceId) return ref.referenceId;
    if (
      (ref.sourceType === SourceType.COMMENT || ref.sourceType === SourceType.SALE_COMMENT) &&
      activeLastOrder?.order?.referenceNo
    ) {
      return activeLastOrder.order.referenceNo;
    }
    return fallbackComment?.commentId ?? null;
  };

  if (!customer) {
    return (
      <div className="hidden md:flex flex-col w-80 border-l border-gray-200">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-8">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">👤</span>
            </div>
            <p className="text-gray-500 text-sm">Select a customer to view details</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col xl:h-full">
      <div className="flex-shrink-0 border-b border-gray-200 px-6 py-3 bg-gray-50">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">Customer Details</h3>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-blue-100 rounded-full"
              title="View Customer"
              aria-label="View Customer"
              onClick={() => navigate(routes.customer.detail(customer.id))}
            >
              <Eye className="h-4 w-4 text-blue-600" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-blue-100 rounded-full"
              title="Edit Profile"
              aria-label="Edit Profile"
              onClick={() => setIsOpenEditCustomerDialog(true)}
            >
              <FileText className="h-4 w-4 text-blue-600" />
            </Button>
          </div>
        </div>
      </div>

      {/* Customer Info */}
      <div className="flex-shrink-0 border-b border-gray-200 px-6 py-5">
        <div className="flex items-center mb-5">
          <div className="mr-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center overflow-hidden">
              {currentCustomer?.profileImageUrl ? (
                <img className="w-14 h-14 object-cover" src={currentCustomer.profileImageUrl} />
              ) : (
                <span className="text-lg font-semibold text-white">{customer.name?.charAt(0)}</span>
              )}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-base font-semibold text-gray-900 truncate">{customer.name}</h4>
            <p className="text-xs text-gray-500 flex items-center mt-0.5">
              <Calendar className="w-3 h-3 mr-1" />
              Customer since{' '}
              {typeof customer.createdAt === 'string'
                ? new Date(customer.createdAt).toLocaleDateString('en-US', {
                  month: 'short',
                  year: 'numeric',
                })
                : 'Unknown'}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Phone className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Phone</p>
              <p className="text-sm text-gray-600 break-all">{customer.phone || 'Not provided'}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-4 h-4 text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Shipping Address</p>
              <p className="text-sm text-gray-600 whitespace-pre-line">
                {customer.address || 'Not provided'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Below xl the whole panel scrolls naturally; at xl: current/last order + flexible history */}
      <div className="xl:flex-1 xl:overflow-hidden flex flex-col xl:min-h-0">
        <div className="flex flex-col xl:min-h-0 xl:flex-1">
          {/* Current / Last Order Header */}
          <div className="px-6 py-3 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                Current / Last Order
                <button
                  className="p-1.5 rounded-full hover:bg-gray-200 transition-colors"
                  title="Add New Order"
                  onClick={() => setIsOpenOrderDialog(true)}
                >
                  <Plus className="w-4 h-4 text-gray-600" />
                </button>
              </h3>
              <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                {activeLastOrder ? '1 order' : '0 orders'}
              </span>
            </div>
          </div>

          {/* Current / Last Order + Comment References */}
          <div className="xl:max-h-72 xl:overflow-y-auto px-6 py-3 border-b border-gray-200 flex-shrink-0">
            {loadingOrders && !activeLastOrder ? (
              <div className="text-sm text-gray-500" role="status">Loading orders…</div>
            ) : ordersError && !activeLastOrder ? (
              <div className="text-sm text-red-600" role="alert">
                Unable to load orders. <button type="button" onClick={() => void refreshOrders()}>Retry</button>
              </div>
            ) : activeLastOrder ? (
              <div className="space-y-3">
                <OrderChatCell
                  key={activeLastOrder.order?.id ?? 'active-last-order'}
                  orderWithRelation={activeLastOrder}
                  onView={onViewOrder}
                  onConfirm={onConfirmOrder}
                  onViewInvoice={onViewReceipt}
                  onAssignUser={onOpenAssignUser}
                />

                {/* Render order references as comments */}
                {lastOrderReferences.length > 0 && (
                  <div className="space-y-2 pt-1">
                    <div className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
                      <MessageCircle className="w-3.5 h-3.5 text-blue-600" />
                      Order Reference Comments ({lastOrderReferences.length})
                    </div>
                    {lastOrderReferences.map((ref) => {
                      const isComment =
                        ref.sourceType === SourceType.COMMENT ||
                        ref.sourceType === SourceType.SALE_COMMENT;
                      const commentId = isComment ? resolveCommentIdForRef(ref) : null;
                      const isReplyingThis = commentId && replyingCommentId === `${ref.id}-${commentId}`;

                      return (
                        <div
                          key={ref.id}
                          className="rounded-lg border border-gray-200 bg-gray-50/80 p-2.5 text-xs space-y-1.5"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <Badge
                                variant="secondary"
                                className={
                                  isComment
                                    ? 'bg-purple-100 text-purple-700 border-purple-200 text-[10px] px-1.5 py-0'
                                    : 'bg-blue-100 text-blue-700 border-blue-200 text-[10px] px-1.5 py-0'
                                }
                              >
                                {isComment ? (
                                  <MessageCircle className="w-2.5 h-2.5 mr-1" />
                                ) : (
                                  <MessageSquare className="w-2.5 h-2.5 mr-1" />
                                )}
                                {ref.sourceType}
                              </Badge>
                              {ref.productCode && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-mono">
                                  {ref.productCode}
                                </Badge>
                              )}
                            </div>
                            <span className="text-[10px] text-gray-400 shrink-0">
                              {ref.timeStamp ? formatOrderDate(ref.timeStamp) : ''}
                            </span>
                          </div>

                          <p className="text-gray-800 break-words leading-snug">{ref.message}</p>

                          {isComment && commentId && !isReplyingThis && (
                            <div className="flex justify-end">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-[11px] text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => setReplyingCommentId(`${ref.id}-${commentId}`)}
                              >
                                <Reply className="w-3 h-3 mr-1" />
                                Reply comment
                              </Button>
                            </div>
                          )}

                          {isReplyingThis && commentId && (
                            <ReplyComponent
                              authorName={customer.name || currentCustomer?.name || 'Customer'}
                              commentId={commentId}
                              compact={true}
                              defaultReply={DEFAULT_COMMENT_REPLY}
                              onClose={() => setReplyingCommentId(null)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex py-4 items-center justify-center">
                <div className="text-center space-y-3">
                  <span className="text-sm text-gray-500">No Active Order</span>
                  <div>
                    <Button
                      variant="default"
                      onClick={() => setIsOpenOrderDialog(true)}
                      className="bg-gradient-to-r from-blue-600 to-blue-800"
                    >
                      <Plus size={16} className="mr-1" /> Create New Order
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Unified Order History (all remaining orders ordered by date) */}
          <div className="flex flex-col xl:flex-1 xl:min-h-0 overflow-hidden">
            <div className="px-6 py-3 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">Order History</h3>
                <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                  {historyOrders.length} orders
                </span>
              </div>
            </div>

            <div className="xl:flex-1 xl:min-h-0 xl:overflow-y-auto px-6 py-3">
              {historyOrders.length > 0 ? (
                <div className="space-y-3">
                  {historyOrders.map((o) => (
                    <OrderChatCell
                      key={o.order?.id ?? Math.random()}
                      orderWithRelation={o}
                      onView={onViewOrder}
                      onConfirm={onConfirmOrder}
                      onViewInvoice={onViewReceipt}
                      onAssignUser={onOpenAssignUser}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-xs text-gray-500">
                  {loadingOrders ? 'Loading orders…' : ordersError ? 'Order history unavailable' : 'No Order History'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <CreateOrderDialog
        currentCustomer={customer}
        isOpenning={isOpenOrderDialog}
        onCloseDialog={() => setIsOpenOrderDialog(false)}
        onSubmitOrder={onSubmitOrder}
      />
      <ViewOrderDialog
        data={selectedOrderRelation}
        open={isOpenViewOrderDialog}
        onClose={() => setIsOpenViewOrderDialog(false)}
        onOpenChange={setIsOpenViewOrderDialog}
      />
      <InvoiceDialog
        data={selectedOrderRelation}
        open={isOpenInvoiceDialog}
        onOpenChange={setIsOpenInvoiceDialog}
      />
      <ReceiptDialog
        data={selectedOrderRelation}
        open={isOpenReceiptDialog}
        onOpenChange={setIsOpenReceiptDialog}
      />
      {selectedOrderRelation && (
        <AssignUserDialog
          open={isOpenAssignDialog}
          onOpenChange={setIsOpenAssignDialog}
          order={selectedOrderRelation.order}
          customer={selectedOrderRelation.customer || customer}
          onSubmit={handleAssignUserSubmit}
        />
      )}
      <CustomerEditDialog
        customer={customer!}
        onOpenChange={setIsOpenEditCustomerDialog}
        open={isOpenEditCustomerDialog}
        onSaved={onSaveCustomer}
      ></CustomerEditDialog>
    </div>
  );
}
